import { LocaleLink as Link, useLocaleNavigate } from '../i18n/locale-link';
import { useLocale, useT } from '../i18n/locale-context';
import { useHydrated } from '../lib/use-hydrated';
import type { CatalogItem } from '../lib/catalog-client';
import { tenderPath } from '../lib/slug';
import { noWinnerOutcomeKey } from '../lib/tender-status';
import {
  CURRENCY_FIRST,
  daysLeft,
  daysWord,
  formatBidSecurity,
  formatBudget,
  formatBudgetCompact,
  formatDeadline,
  statusKind,
  withCurrency,
} from '../lib/format';
import { BankIcon, ClockIcon } from './icons';
import { OriginalTitle } from './original-title';
import { SaveStar } from './save-star';
import { LockGlyph } from './locked-chip';
import { BlurredValue } from './blurred-value';
// Blurred stand-in for a walled winner on a list row — never real data.
import { LOCKED_WINNER_PLACEHOLDER } from '../lib/locked-placeholders';
import { outcomeLockText } from '../lib/outcome-lock-text';
import { plansPathWithContext } from '../lib/pending-checkout';
import { track } from '../lib/funnel';
import { positionPreviewLabel, positionsCount } from '../lib/lot-positions';
import { countryConfig } from '../country';

// Shared column template so the desktop header aligns with each row (and the
// loading skeleton in `query-error.tsx` mirrors the same three-column grid).
export const GRID_COLS = 'md:grid-cols-[1fr_155px_125px]';

// A region/category tag pill (shared by the row's meta line).
const tagClass =
  'rounded-pill border border-edge bg-bone px-2 py-0.5 text-[11px] font-semibold text-ink-soft transition-colors group-hover:border-brand/20 group-hover:bg-brand-dim group-hover:text-brand';

/** Тип нотиса института → ключ локализации. Неизвестный код не показываем
 *  вообще: лучше пустая ячейка, чем сырой машинный код в интерфейсе. */
const NOTICE_TYPE_KEYS: Record<string, string> = {
  ifb: 'tender.notice.ifb',
  reoi: 'tender.notice.reoi',
  gpn: 'tender.notice.gpn',
  advance: 'tender.notice.advance',
  firm: 'tender.notice.firm',
};

/**
 * Spain: the only announcement source is the PLACSP anuncio previo (status PRE), and
 * its card said «Abierta» over «Pendiente de convocatoria» (QA 2026-10-21) — the pill
 * names the stage instead. UZ/KZ announcements (xt-xarid) keep the status pill. A
 * consulta preliminar del mercado (PLACSP 1403, `item.consultation`) is named the same
 * way while it is live.
 */
const PRIOR_NOTICE_PILL = countryConfig.code === 'es';

/** The stage a live Spanish card names instead of «Abierta» (PLACSP 643 PRE / 1403). */
type StagePill = 'priorNotice' | 'consultation' | null;

function StatusCell({ status, stage = null }: { status: string; stage?: StagePill }) {
  const t = useT();
  const kind = statusKind(status);
  const dot =
    kind === 'closing'
      ? 'bg-status-closing-dot shadow-[0_0_0_3px_rgba(245,158,11,0.15)]'
      : kind === 'closed'
        ? 'bg-ink-soft'
        : 'bg-status-active-dot shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';
  const pill =
    kind === 'closing'
      ? 'bg-status-closing-bg text-status-closing-text'
      : kind === 'closed'
        ? 'bg-edge text-ink-muted'
        : 'bg-status-active-bg text-status-active-text';
  // A closed consulta reads «Cerrada» like any other closed lot (the anuncio previo flag
  // only ever reaches a live row — `isLiveAnnouncement`).
  const label =
    stage === 'priorNotice'
      ? t('tender.notice.advance')
      : stage === 'consultation' && kind !== 'closed'
        ? t('tender.notice.consultation')
        : t(`tender.status.${kind}`);
  return (
    <div className="flex items-center gap-2">
      <span className={`mt-px h-[7px] w-[7px] shrink-0 rounded-full ${dot}`} />
      <span className={`rounded-pill px-2 py-0.5 text-[11px] font-bold leading-[1.5] ${pill}`}>
        {label}
      </span>
    </div>
  );
}

/**
 * «Позиции: стол для учителя 713 шт, доска меловая 324 шт … всего 24» — what
 * an awarded lot bought, on its /results row (owner, 2026-10-17). The API
 * sends `positions` on the awarded list only, as a preview: the first few
 * names with quantities (no prices) and `positionsTotal` when the lot has
 * more, so every other list renders exactly what it rendered before.
 * A single nameless entry is a one-position lot whose name is the heading —
 * it reads «Количество: 20 шт». Plain text: the whole row is already a link.
 */
function PositionsPreview({ item, compact }: { item: CatalogItem; compact: boolean }) {
  const t = useT();
  const list = item.positions ?? [];
  const labels = list.map(positionPreviewLabel).filter((s): s is string => !!s);
  if (labels.length === 0) return null;
  const count = positionsCount(list, item.positionsTotal);
  const qtyOnly = list.length === 1 && !list[0]?.name?.trim();
  return (
    <div
      data-testid="positions-preview"
      className={`text-[12.5px] leading-[1.45] text-ink-soft ${compact ? 'line-clamp-1' : 'line-clamp-2'}`}
    >
      <span className="font-medium text-ink-muted">
        {t(qtyOnly ? 'list.positions.qty' : 'list.positions.label')}:
      </span>{' '}
      {labels.join(', ')}
      {count > labels.length && ` ${t('list.positions.more', { n: count })}`}
    </div>
  );
}

/**
 * What the right-hand date column of a list shows. `'deadline'` (the default,
 * every list on UZ/KZ) is the bid deadline. `'awarded'` is the AWARD date — for
 * a list of awarded lots, where the old bid deadline answers no question
 * (MiLicita /results, audit 2026-10-21: «Plazo» showed deadlines weeks before
 * the award). `awardedAt` is public; the outcome wall never strips it.
 */
export type ListDateColumn = 'deadline' | 'awarded';

/**
 * A single m16 tender row. The whole row links to the detail page.
 *
 * Responsive: below `md` it renders as a stacked card (status → title → tags →
 * summary snippet, then a divided meta footer with budget + deadline as
 * label/value rows). At `md` and up it becomes the three-column table row
 * (title | budget | deadline). Every field, link and status pill is preserved
 * in both layouts — only the arrangement changes.
 *
 * `note` / `isNew` are the ONE extension point, added for «Мои тендеры»: that
 * page shows the same card and needs to say which saved search put it there and
 * whether the reader has seen it. They are optional and inert everywhere else,
 * so the catalog, /results, /ifi and ☆ Избранное render byte-for-byte what they
 * rendered before. A whole second card component for two lines of muted text
 * would have been the third place a tender row is defined. `dateColumn` is the
 * second, for MiLicita's /results ({@link ListDateColumn}); same rule.
 */
export function TenderRow({
  item,
  compact = false,
  note,
  isNew = false,
  dateColumn = 'deadline',
}: {
  item: CatalogItem;
  compact?: boolean;
  /** Muted one-liner under the title — «по поиску: …» on «Мои тендеры». */
  note?: string;
  /** Render the «новое» marker. */
  isNew?: boolean;
  /** See {@link ListDateColumn}. */
  dateColumn?: ListDateColumn;
}) {
  const t = useT();
  const { locale } = useLocale();
  const isIfi = item.scope === 'ifi';
  // Supply-scope (cooperation.uz leads) surfaces in mixed search results too —
  // badge it so it isn't mistaken for a budgeted gov lot. Gov itself is never
  // badged: it's the default majority, so a «gov» pill would be noise on almost
  // every card — the absence of an МФИ/supply badge already means gov.
  const isSupply = item.scope === 'supply';
  const noticeKey = item.noticeType ? NOTICE_TYPE_KEYS[item.noticeType] : undefined;
  // В слое МФИ бюджета в сумах не существует — на его месте тип нотиса. For gov
  // budgets show the compact form ("110 млн" / "1,9 млрд") for scannability,
  // matching the detail rail; the exact grouped value rides in a hover tooltip.
  const budget = isIfi
    ? noticeKey
      ? t(noticeKey)
      : ''
    : formatBudgetCompact(item.budgetUzs, locale);
  const budgetExact = isIfi ? null : formatBudget(item.budgetUzs);
  // An awarded row leads with the FINAL price when the API sent one. Since the
  // outcome wall (2026-10-07) it sends it — and the winner — to a Pro viewer
  // only; everyone else gets `outcomeLocked`, so this falls back to the
  // starting budget and the lock line below says what is behind the wall.
  const finalPrice =
    !isIfi && item.finalPriceUzs ? formatBudgetCompact(item.finalPriceUzs, locale) : '';
  const finalExact = finalPrice ? formatBudget(item.finalPriceUzs) : null;
  // MiLicita: a lot with neither a budget nor a final price shows its
  // expediente's total, labelled as such, before falling back to «No indicado».
  // The API sends `expedienteBudgetUzs` for PLACSP lots only (absent on UZ/KZ).
  const expedienteTotal =
    !isIfi && !finalPrice && !budget && item.expedienteBudgetUzs
      ? (formatBudgetCompact(item.expedienteBudgetUzs, locale) ?? '')
      : '';
  const expedienteExact = expedienteTotal ? formatBudget(item.expedienteBudgetUzs) : null;
  /* «300 тыс. сум» when the source published an amount, a bare «3 %» when it
     published a RATE. This used to be a plain `formatBudget` of the raw column,
     so a 1,2-млрд lot whose source publishes «3%» rendered «Обеспечение: 3»,
     and a 0,5% rate rendered «05» (digit-stripping eats the decimal point).
     No `approxAmount` here: the computed «≈ 12 млн сум» wrapped this 11px line
     onto two rows next to the budget it was being confused with. The detail
     page carries it instead. See the unit note in `lib/format.ts`.
     `publishedPct` keeps the «3 %» once the API stores that rate as so'm. */
  const bidSecurity = formatBidSecurity(
    item.bidSecurityUzs,
    item.budgetUzs,
    locale,
    t('tender.budget.unit'),
    { compact: true, publishedPct: item.bidSecurityPct },
  );
  const awardColumn = dateColumn === 'awarded';
  const deadline = formatDeadline(item.deadline, locale);
  const awardedOn = awardColumn ? formatDeadline(item.awardedAt, locale) : null;
  // «Анонс»: announced, bids not open yet, so no deadline exists to print —
  // a badge takes its place instead of «Уточняется», which reads as a data gap.
  const announcement = !awardColumn && item.announcement === true;
  const urgent = !awardColumn && statusKind(item.status) === 'closing';
  const tags = item.categories.length > 0 ? item.categories.map((c) => c.name) : [];
  // Client-only countdown — see useHydrated: the catalogue and category pages
  // are prerendered and must not bake a now-relative number into the HTML.
  const hydrated = useHydrated();
  const navigate = useLocaleNavigate();
  const dLeft = hydrated && !awardColumn ? daysLeft(item.deadline) : null;

  // Every card is a real, clickable card on every layer. `ifi` (donor-financed)
  // rows used to render here as inert, dimmed teasers with no link through — that
  // whole-layer wall is gone (the API now serves donor rows like gov rows), so
  // there is no locked variant left.
  const rootClassName = `group relative flex cursor-pointer flex-col border-b border-edge-light px-4 transition-colors last:border-b-0 hover:bg-bone-hover md:grid ${GRID_COLS} md:items-center md:px-5 ${
    compact ? 'gap-1.5 py-2 md:gap-2' : 'gap-2.5 py-4 md:gap-3'
  }`;

  const content = (
    <>
      {/* Main column: status, title, tags, summary snippet. */}
      {/* Main column: the text stacks, the star sits beside it, centred on the
          whole stack.
          ONE structure for both densities. The star used to live on the status
          line in normal and on the title line in compact, so in each mode it
          aligned to whichever line it happened to share — which reads as
          misaligned as soon as anything follows underneath. It is an action on
          the ROW, so it centres on the row. */}
      <div className="flex min-w-0 items-center gap-2">
        <div className={`flex min-w-0 flex-1 flex-col ${compact ? 'gap-[2px]' : 'gap-[6px]'}`}>
          {/* The pill goes in compact — the deadline colour already carries
              urgency, and a row should spend no vertical space on chrome. */}
          {!compact && (
            <div className="flex items-center gap-2">
              <StatusCell
                status={item.status}
                stage={
                  PRIOR_NOTICE_PILL && announcement
                    ? 'priorNotice'
                    : PRIOR_NOTICE_PILL && !awardColumn && item.consultation === true
                      ? 'consultation'
                      : null
                }
              />
              {/*
               * «Заявка готова» — only on a row whose payload says a package is
               * BUILT, which today is only `/me/saved-tenders` (☆ Избранное).
               * Every other list leaves the field absent and renders exactly
               * what it rendered before.
               *
               * A real `<a href>`, not a button that fetches a blob: this cohort
               * reads on a phone, in-app WebViews drop blob downloads silently,
               * and that failure has already been fixed twice on this codebase
               * (`lib/save-blob.ts`). `stopPropagation` and nothing else — the
               * whole row is a <Link>, so the click has to be kept from it while
               * the anchor's own navigation is left alone.
               */}
              {item.bidPackageReady && (
                <a
                  href={item.bidPackageReady.downloadUrl}
                  target="_blank"
                  // nofollow for the same reason the documents card uses it: a
                  // download endpoint is not a page, and Googlebot has followed
                  // one of ours before.
                  rel="noopener noreferrer nofollow"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 rounded-pill bg-brand-light px-2 py-0.5 text-[11px] font-bold leading-[1.5] text-brand underline decoration-brand/30 underline-offset-2"
                >
                  {t('list.bidPackageReady')}
                </a>
              )}
              {/* `ml-auto` on mobile puts it at the card's right edge; at md+
                  the row is a grid cell whose width the title governs, so a
                  right-pushed star would float far from the pill it belongs
                  with. There it just follows the pill. */}
              <SaveStar tenderId={item.id} className="ml-auto shrink-0 md:ml-0" />
            </div>
          )}
          <div className="flex items-start gap-2">
            {/*
             * An <h3>, not a <div>. The card title is styled as a heading and read
             * as one, but carried no heading semantics — so every listing page on
             * the site had exactly one heading (the sr-only <h1>) and zero <h2>/<h3>
             * (measured 2026-08-12 on /, /prices, /results, /page/N, /ifi and four
             * category and region hubs: h1:1, h2:0 on all of them). The homepage
             * exposes 20 tender titles this way and none of them were headings, so
             * crawlers and AI extractors had no document outline to work from on the
             * page type that is 91% of the sitemap.
             *
             * h3 rather than h2: the hub pages use h2 for their own section headings
             * (see /market, /guide), so h3 keeps the outline properly nested rather
             * than putting 20 sibling h2s under a single h1.
             *
             * Purely semantic — the class list is unchanged, so this renders
             * identically. Tailwind supplies every type style here; nothing depends
             * on the browser's default heading styles.
             */}
            <h3
              className={`min-w-0 flex-1 font-semibold leading-[1.4] text-ink transition-colors group-hover:text-brand ${
                compact
                  ? 'line-clamp-3 text-[13.5px] md:text-[13px]'
                  : 'line-clamp-3 text-[15px] md:line-clamp-2 md:text-[14px]'
              }`}
            >
              {item.title}
            </h3>
            {/* «новое» — a dot, not a badge, and it sits with the title rather
                than in the status column: the status pill answers "can I still
                bid", this answers "have I read it", and stacking them in one
                place made both harder to scan. The visible glyph is decorative;
                the word rides along for screen readers, which cannot see a
                coloured circle. */}
            {isNew && (
              <span className="mt-[5px] flex shrink-0 items-center gap-1">
                <span
                  className="h-[7px] w-[7px] rounded-full bg-brand shadow-[0_0_0_3px_rgba(15,138,138,0.15)]"
                  aria-hidden
                />
                <span className="sr-only">{t('myTenders.new')}</span>
              </span>
            )}
          </div>
          {/* Spain (`content.castilianTitles`): the platform's own title of a lot shown in Castilian. */}
          <OriginalTitle title={item.originalTitle} className="line-clamp-1" />
          {note && <div className="text-[12px] italic text-ink-muted">{note}</div>}
          {item.buyerName && (
            <div className="flex items-center gap-[5px] text-[12.5px] text-ink-soft">
              <BankIcon className="h-3.5 w-3.5 shrink-0 opacity-60" />
              <span className="line-clamp-1">{item.buyerName}</span>
            </div>
          )}
          {/* The award. Pro-only since 2026-10-07 (the outcome wall): the API
              sends `winnerName` to a Pro viewer, and `outcomeLocked` to
              everyone else — a guest or free reader sees that the lot WAS
              awarded and that who/how much is in Pro, never the name. Plain
              text, not a link: the whole row is already one. */}
          {/* The shared hidden-data chip (`locked-chip.tsx`) — the same pill
              and padlock as the lot page's outcome wall and the locked
              analytics teasers. */}
          {/* The chip is a way to /plans (2026-10-07 review: ad traffic lands
              on /results, and the owner keeps guest checkout) — counted as
              `upgrade_cta_clicked{source:'resultsRow'}`. A <button>, not an
              <a>: the whole row is already a <Link>, and a nested anchor is
              re-parented by the HTML parser, which breaks the prerendered
              row's hydration. Same pattern as `SaveStar`. */}
          {item.outcomeLocked && !item.winnerName && (
            <div className="flex">
              <button
                type="button"
                data-testid="outcome-locked-row-cta"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  track('upgrade_cta_clicked', { source: 'resultsRow' });
                  void navigate(plansPathWithContext('paywall', item.id));
                }}
                className="max-w-full rounded-md text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {/* Igor, 10.10: the same lock as every other closed block —
                    the Pro row's own «Победитель: …» line with the name
                    blurred and the 🔒 after it, instead of a sentence in a
                    chip. The placeholder is fixed text (no winner is in the
                    payload); the sentence stays for screen readers. */}
                <span
                  data-testid="outcome-locked-row"
                  className="flex min-w-0 items-center gap-[5px] text-[12.5px] text-ink-soft transition-colors hover:text-brand"
                >
                  <span className="shrink-0 font-medium text-ink-muted">
                    {t('tender.outcome.winner')}:
                  </span>
                  <BlurredValue className="truncate">{LOCKED_WINNER_PLACEHOLDER}</BlurredValue>
                  <LockGlyph className="shrink-0 text-[12px]" />
                  <span className="sr-only">{outcomeLockText(t, item, 'row')}</span>
                </span>
              </button>
            </div>
          )}
          {item.winnerName && (
            <div className="flex items-center gap-[5px] text-[12.5px] text-ink-soft">
              <span className="shrink-0 font-medium text-ink-muted">
                {t('tender.outcome.winner')}:
              </span>
              <span className="line-clamp-1">
                {item.winnerName}
                {item.winnerInn ? ` · ${item.winnerInn}` : ''}
              </span>
            </div>
          )}
          {/* «Закупка не состоялась» — a free public record for everyone (the
              outcome wall never applies to it). A `'failed'` lot never carries `winnerName`
              (see catalog.service.ts `toTenderView`), so this is its only
              tell on the card; without it the card reads as an ordinary
              expired lot. */}
          {!item.winnerName && item.outcomeStatus === 'failed' && (
            <div className="text-[12.5px] font-medium text-ink-soft">
              {t(noWinnerOutcomeKey(item.outcomeReason))}
            </div>
          )}
          <PositionsPreview item={item} compact={compact} />
          {!compact && (isIfi || isSupply || item.region || tags.length > 0) && (
            <div className="flex flex-wrap items-center gap-[5px]">
              {/* Layer tag — for the non-default layers (МФИ, supply), and only
                  because a search now spans every layer (catalog.tsx sends
                  scope=all when a query is present). Browsing /ifi or /supply is
                  self-labelling: the whole page is that layer. A mixed search
                  result is not — without this an МФИ row reads as a broken gov
                  lot (no som budget, notice type where the sum should be), and a
                  supply lead as a budgeted tender it isn't. */}
              {isIfi && (
                <span className="rounded-pill bg-brand-light px-2 py-0.5 text-[11px] font-bold leading-[1.5] text-brand">
                  {t('ifi.badge')}
                </span>
              )}
              {isSupply && (
                <span className="rounded-pill border border-edge bg-bone px-2 py-0.5 text-[11px] font-bold leading-[1.5] text-ink-soft">
                  {t('supply.badge')}
                </span>
              )}
              {item.region && <span className={tagClass}>{item.region}</span>}
              {tags.map((tag) => (
                <span key={tag} className={tagClass}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          {/* Compact drops the description and the tag strip — the two longest,
            least scannable parts of a row. Status, title, buyer, budget and
            deadline all stay: those are what a row is FOR, and a density
            toggle that hides them would be a different list, not a tighter
            one. */}
          {!compact && item.description && (
            <div className="text-[13px] leading-[1.5] text-ink-muted md:text-[12.5px]">
              <span className="line-clamp-2">{item.description}</span>
            </div>
          )}
        </div>
        {/* Compact has no star at any width: saving is a deliberate act with
            a home on the tender page, and in a scan-dense list it was one more
            target per row competing with the row's own click. Normal mode
            keeps it, up on the status line. */}
      </div>

      <div
        className={
          compact
            ? 'flex flex-row-reverse items-baseline justify-between gap-3 md:contents'
            : 'contents'
        }
      >
        {/* Budget: a label/value row on mobile, a right-aligned column at md+. */}
        <div
          className={`flex items-baseline justify-between gap-3 md:block md:border-0 md:text-right ${
            compact ? 'justify-start' : 'border-t border-edge-light pt-2.5'
          }`}
        >
          <span
            className={`text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-muted md:hidden ${compact ? 'hidden' : ''}`}
          >
            {/* An IFI card shows a notice type in this column, not a som budget —
              don't caption it "budget" on mobile where the label sits right
              above the value. */}
            {t(
              isIfi
                ? 'list.col.noticeType'
                : finalPrice
                  ? 'tender.outcome.finalPrice'
                  : expedienteTotal
                    ? 'tender.budget.expedienteTotal'
                    : 'list.col.budget',
            )}
          </span>
          <span className="flex min-w-0 flex-col items-end gap-[2px]">
            {finalPrice || budget || expedienteTotal ? (
              <span className="flex items-baseline gap-1 md:flex-col md:items-end md:gap-[2px]">
                {(finalPrice || expedienteTotal) && (
                  <span className="hidden text-[10.5px] font-semibold uppercase tracking-[0.04em] text-ink-muted md:block md:leading-[13px]">
                    {t(finalPrice ? 'tender.outcome.finalPrice' : 'tender.budget.expedienteTotal')}
                  </span>
                )}
                <span
                  title={
                    (finalPrice ? finalExact : expedienteTotal ? expedienteExact : budgetExact) ??
                    undefined
                  }
                  className="text-[14px] font-bold tabular-nums tracking-[-0.2px] text-brand md:text-[13.5px] md:leading-[17px]"
                >
                  {/* Chile: «$ 1,2 M» on one line — the peso sign leads (`CURRENCY_FIRST`). */}
                  {CURRENCY_FIRST && !isIfi
                    ? withCurrency(finalPrice || budget || expedienteTotal, t('tender.budget.unit'))
                    : finalPrice || budget || expedienteTotal}
                </span>
                {/* The currency unit only makes sense next to an actual sum figure —
                  an IFI row shows a notice-type label here instead, so skip it. */}
                {!isIfi && !CURRENCY_FIRST && (
                  <span className="text-[11px] font-medium text-ink-muted md:leading-[14px]">
                    {t('tender.budget.unit')}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-[12.5px] text-ink-muted">{t('tender.budget.undisclosed')}</span>
            )}
            {bidSecurity && (
              <span className="text-[11px] font-medium text-ink-muted">
                {t('tender.bidSecurity')}: {bidSecurity}
              </span>
            )}
          </span>
        </div>

        {/* Deadline: a label/value row on mobile, a right-aligned column at md+. */}
        <div className="flex items-baseline justify-between gap-3 md:flex-col md:items-end md:gap-[3px] md:text-right">
          <span
            className={`text-[11px] font-medium uppercase tracking-[0.04em] text-ink-muted md:hidden ${compact || announcement ? 'hidden' : ''}`}
          >
            {t(awardColumn ? 'tender.outcome.awardedAt' : 'tender.deadline.closes')}
          </span>
          <span className="flex flex-col items-end gap-[1px] md:gap-[2px]">
            {awardColumn ? (
              <span
                className="text-[13px] font-semibold text-ink md:leading-[17px]"
                data-testid="row-awarded-at"
              >
                {awardedOn ?? '—'}
              </span>
            ) : announcement ? (
              <span className="rounded-pill bg-brand-dim px-2 py-0.5 text-[11px] font-bold text-brand md:leading-[14px]">
                {t('tender.deadline.announcement')}
              </span>
            ) : (
              <span
                className={`text-[13px] font-semibold md:leading-[17px] ${urgent ? 'text-status-closing-text' : 'text-ink'}`}
              >
                {deadline ?? t('tender.deadline.tba')}
              </span>
            )}
            {dLeft !== null && (
              <span
                className={`flex items-center gap-[3px] text-[11px] font-medium md:leading-[14px] ${urgent ? 'text-status-closing-text' : 'text-ink-muted'}`}
              >
                <ClockIcon className="h-3 w-3 opacity-70" />
                {t('tender.daysLeftText', { days: `${dLeft} ${daysWord(dLeft, locale)}` })}
              </span>
            )}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <Link to={tenderPath(item.slug, item.id)} className={rootClassName}>
      {content}
    </Link>
  );
}

/** Per-row extras for a list that knows something the card does not. */
export interface TenderRowAnnotation {
  /** Muted one-liner under the title. */
  note?: string;
  /** Render the «новое» marker. */
  isNew?: boolean;
}

/** m16 tender list: surface card with a header row and tender rows. */
export function TenderList({
  items,
  compact = false,
  annotate,
  dateColumn = 'deadline',
}: {
  items: CatalogItem[];
  compact?: boolean;
  /** See {@link ListDateColumn}. Header and every row follow it. */
  dateColumn?: ListDateColumn;
  /**
   * Per-row extras, resolved by the page rather than the list — «Мои тендеры»
   * is the only caller and it is the only place that knows which saved search
   * matched a lot. A callback, not a prop on the item, so the shared
   * `CatalogItem` type does not grow a field that means nothing on /catalog.
   */
  annotate?: (item: CatalogItem) => TenderRowAnnotation;
}) {
  const t = useT();
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-edge bg-bone-surface p-8 text-center text-[13px] text-ink-muted shadow-m-sm">
        {t('list.empty')}
      </div>
    );
  }
  // A route ever renders one layer's cards at a time (the ifi route fixes
  // `scope=ifi`; the plain catalog excludes it) — the header is this list's
  // twin of TenderRow's per-row mobile label: same defect (a "Бюджет" column
  // full of notice-type text), same fix, derived from the actual rows rather
  // than a separate scope prop so it can never drift from what's rendered.
  const isIfi = items[0]?.scope === 'ifi';
  return (
    <div className="overflow-hidden rounded-lg border border-edge bg-bone-surface shadow-m-sm">
      {/* Column header — desktop table only; mobile cards are self-labelling. */}
      <div className={`hidden ${GRID_COLS} border-b border-edge bg-bone px-5 py-[9px] md:grid`}>
        <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
          {t('list.col.tender')}
        </div>
        <div className="text-right text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
          {t(isIfi ? 'list.col.noticeType' : 'list.col.budget')}
        </div>
        <div className="text-right text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted">
          {t(dateColumn === 'awarded' ? 'list.col.awarded' : 'list.col.deadline')}
        </div>
      </div>
      {items.map((item) => {
        const extra = annotate?.(item);
        return (
          <TenderRow
            key={item.id}
            item={item}
            compact={compact}
            note={extra?.note}
            isNew={extra?.isNew}
            dateColumn={dateColumn}
          />
        );
      })}
    </div>
  );
}
